import { Button } from '$components'
import { FormFieldGroup, FormInput, FormLabel, FormDatePicker, FormTextarea, FormRadioGroup, FormLabelDescription } from '$components'
import { FormSelect } from '$components'
import { Card } from '$components'
import { Text } from '$components'
import { PlusIcon } from '$components'
import { z } from 'zod'
import { Input } from './types'
import { useFieldArray } from 'react-hook-form'
import { DogBreed, Gender } from '$api-client/types.gen'
import { Fragment, useState } from 'react'
import { useInfiniteSizeCategoryOptions } from '$hooks/use-infinite-size-category-options'
import { useInfiniteCoatTypeOptions } from '$hooks/use-infinite-coat-type-options'
import { PetOptionSettingsLink } from '$components'

const petItemSchema = z.object({
  [Input.PetName]: z.string()
    .min(1, { message: 'Name is required.' })
    .max(255, { message: 'Name must be less than 255 characters.' }),
  [Input.PetGender]: z.nativeEnum(Gender, {
    required_error: 'Gender is required.',
    invalid_type_error: 'Gender is required.',
  }),
  [Input.PetBreed]: z.object({
    id: z.string(),
    value: z.string(),
  }, {
    required_error: 'Breed is required.',
  }),
  [Input.PetBirthDate]: z.string()
    .optional(),
  [Input.PetSizeCategory]: z.object({
    id: z.string(),
    value: z.string(),
  }).optional(),
  [Input.PetCoatType]: z.object({
    id: z.string(),
    value: z.string(),
  }).optional(),
  [Input.PetInternalNotes]: z.string()
    .max(2048, { message: 'Notes must be less than 2048 characters.' })
    .optional(),
})

export const petsSchema = z.object({
  [Input.Pets]: z.array(petItemSchema).optional(),
})

export function PetsStep() {
  const { fields, append, remove } = useFieldArray({ name: Input.Pets })

  const [breedSearch, setBreedSearch] = useState<Record<string, string>>({})
  const { options: sizeOptions, search: sizeSearch } = useInfiniteSizeCategoryOptions()
  const { options: coatOptions, search: coatSearch } = useInfiniteCoatTypeOptions()

  const AddButton = (
    <Button
      type="button"
      variant="secondary"
      size="lg"
      icon={PlusIcon}
      disabled={fields.length >= 5}
      onClick={() => append({})}
      className="w-full bg-white border border-gray-200"
    >
      Add Pet
    </Button>
  )

  return (
    <div className="flex flex-col gap-6">
      { fields.length === 0
        ? (
          <div className="flex flex-col items-center justify-center gap-4 pt-12 pb-8 w-full rounded-3xl bg-neutral-50 p-6">
            <Text type="s">
              Add pets by clicking the button below or add them later.
            </Text>

            { AddButton }
          </div>
        )
        : (
          <Fragment>
            { fields.map((field, index) => {
              return (
                <Card key={field.id}>
                  {/* Header */}
                  <div className="flex items-center justify-between mb-4">
                    <Text weight="medium">
                      {`Pet ${index + 1}`}
                    </Text>

                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => remove(index)}
                    >
                      Remove
                    </Button>
                  </div>

                  {/* Inputs */}
                  <div className="flex flex-col gap-6">
                    <FormFieldGroup>
                      <FormLabel htmlFor={`${Input.Pets}.${index}.${Input.PetName}`}>
                        Name
                      </FormLabel>

                      <FormInput
                        name={`${Input.Pets}.${index}.${Input.PetName}`}
                        placeholder="Enter pet name..."
                      />
                    </FormFieldGroup>

                    <FormFieldGroup>
                      <FormLabel htmlFor={`${Input.Pets}.${index}.${Input.PetBreed}`}>
                        Breed
                      </FormLabel>

                      <FormSelect
                        name={`${Input.Pets}.${index}.${Input.PetBreed}`}
                        placeholder="Select breed..."
                        clearable
                        search={{
                          search: breedSearch[`${Input.Pets}.${index}.${Input.PetBreed}`],
                          onSearchChange: (search) => setBreedSearch({
                            ...breedSearch,
                            [`${Input.Pets}.${index}.${Input.PetBreed}`]: search,
                          }),
                        }}
                        options={Object.entries(DogBreed).map(([id, value]) => ({ id, value }))}
                      />
                    </FormFieldGroup>

                    <FormFieldGroup>
                      <FormLabel htmlFor={`${Input.Pets}.${index}.${Input.PetSizeCategory}`}>
                        Size Category
                      </FormLabel>

                      <FormSelect
                        name={`${Input.Pets}.${index}.${Input.PetSizeCategory}`}
                        placeholder="Select size..."
                        options={sizeOptions}
                        search={sizeSearch}
                      />

                      <PetOptionSettingsLink type="size" />
                    </FormFieldGroup>

                    <FormFieldGroup>
                      <FormLabel htmlFor={`${Input.Pets}.${index}.${Input.PetCoatType}`}>
                        Coat Type
                      </FormLabel>

                      <FormSelect
                        name={`${Input.Pets}.${index}.${Input.PetCoatType}`}
                        placeholder="Select coat type..."
                        options={coatOptions}
                        search={coatSearch}
                      />

                      <PetOptionSettingsLink type="coat" />
                    </FormFieldGroup>

                    <FormFieldGroup>
                      <FormLabel htmlFor={`${Input.Pets}.${index}.${Input.PetBirthDate}`}>
                        Date of Birth
                      </FormLabel>

                      <FormDatePicker name={`${Input.Pets}.${index}.${Input.PetBirthDate}`} />
                    </FormFieldGroup>

                    <FormFieldGroup>
                      <FormLabel htmlFor={`${Input.Pets}.${index}.${Input.PetGender}`}>
                        Gender
                      </FormLabel>

                      <FormRadioGroup
                        name={`${Input.Pets}.${index}.${Input.PetGender}`}
                        options={Object.entries(Gender).map(([id, value]) => ({
                          id,
                          label: value,
                          value,
                        }))}
                      />
                    </FormFieldGroup>

                    <FormFieldGroup>
                      <FormLabel htmlFor={`${Input.Pets}.${index}.${Input.PetInternalNotes}`}>
                        Internal Notes
                      </FormLabel>

                      <FormLabelDescription>
                        Add any internal notes about this pet (only visible to you and your team).
                      </FormLabelDescription>

                      <FormTextarea
                        name={`${Input.Pets}.${index}.${Input.PetInternalNotes}`}
                        placeholder="Enter any internal notes..."
                      />
                    </FormFieldGroup>
                  </div>
                </Card>
              )
            })}

            <div className="flex flex-col items-center justify-center gap-2">
              { fields.length === 5 && (
                <Text
                  type="s"
                  className="text-center"
                >
                  You can only add up to 5 pets.
                </Text>
              )}

              { AddButton }
            </div>
          </Fragment>
        )}
    </div>
  )
}
